import { createFileRoute } from '@tanstack/react-router'
import { businessNumberLabel, localeFor } from '@/lib/locale'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Check, Loader2, ShieldCheck, PenTool, Eraser, Download } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { phFormat, peso } from '@/lib/ph'
import { todayIso } from '@/lib/today'
import { FEATURES } from '@/lib/features'

export const Route = createFileRoute('/q/$token')({ component: PublicQuote })

interface PublicQuoteData {
  quote: {
    quote_number: string | null; title: string | null; status: string; currency: string
    subtotal: number; tax: number; total: number; valid_until: string | null; notes: string | null
    viewed_at: string | null; accepted_at: string | null; declined_at: string | null; signer_name: string | null; decline_reason: string | null
    deposit_amount: number | null; deposit_paid_at: string | null
  }
  company: { name: string | null; logo: string | null; accent_color: string | null; abn: string | null; email: string | null; phone: string | null; country: string | null }
  client: { first_name: string | null; last_name: string | null; organization: string | null }
  items: Array<{
    id: number; description: string; quantity: number; unit: string | null
    unit_price: number; amount: number
    optional: boolean; selected: boolean; tier: string | null
  }>
}

// Spec 001: MySerbisyo bills in pesos only. The currency argument is kept for the call sites.
const money = (n: number, _c?: string) => peso(n)

function PublicQuote() {
  const { token } = Route.useParams()

  const q = useQuery({
    queryKey: ['public-quote', token],
    queryFn: async (): Promise<PublicQuoteData | null> => {
      const { data, error } = await supabase.rpc('public_get_quote', { p_token: token })
      if (error) throw error
      return data as PublicQuoteData | null
    },
  })

  // Spec 021 selection state. Declared here, with the other hooks, because the early returns for
  // loading / not-found sit below and hooks cannot run conditionally.
  const [tier, setTier] = useState<string | null>(null)
  const [picked, setPicked] = useState<Set<number>>(new Set())

  // Default to the middle tier when the quote offers them — the one most customers choose, and the
  // one the trade person is usually steering toward. Never auto-tick an add-on: an opt-in the customer
  // did not make is a charge they did not agree to.
  useEffect(() => {
    if (!q.data) return
    const tiers = [...new Set(q.data.items.filter((i) => i.tier).map((i) => i.tier!))]
    if (tiers.length && tier === null) setTier(tiers[Math.floor((tiers.length - 1) / 2)])
  }, [q.data, tier])

  // Stamp "viewed" once on first load (QUO-13).
  useEffect(() => {
    if (q.data) supabase.rpc('public_mark_quote_viewed', { p_token: token })
  }, [q.data, token])

  const notifyOrg = () => supabase.functions.invoke('quote-decision', { body: { token } }).catch(() => {})

  const payDeposit = useMutation({
    mutationFn: async (p: { signer: string }) => {
      const { data, error } = await supabase.functions.invoke('quote-deposit-checkout', {
        body: { token, signer: p.signer, origin: window.location.origin },
      })
      if (error || !(data as { url?: string })?.url) throw new Error('Could not start the deposit payment — please try again.')
      window.location.href = (data as { url: string }).url
    },
  })
  const depositJustPaid = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('deposit') === '1'

  const accept = useMutation({
    mutationFn: async (p: { signer: string; signature: string }) => {
      // Send WHICH items, never what they cost — the server recomputes the price.
      const { data, error } = await supabase.rpc('public_accept_quote', {
        p_token: token, p_signer: p.signer, p_signature: p.signature,
        p_selected: (q.data?.items ?? []).filter((i) => i.optional && picked.has(i.id)).map((i) => i.id),
        p_tier: tier,
      })
      if (error) throw error
      if (!data) throw new Error('This quote can no longer be accepted (it may have expired).')
    },
    onSuccess: async () => { await notifyOrg(); q.refetch() },
  })

  const decline = useMutation({
    mutationFn: async (p: { reason: string }) => {
      const { data, error } = await supabase.rpc('public_decline_quote', { p_token: token, p_reason: p.reason })
      if (error) throw error
      if (!data) throw new Error('This quote can no longer be declined.')
    },
    onSuccess: async () => { await notifyOrg(); q.refetch() },
  })

  if (q.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-canvas-subtle"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>
  }
  if (!q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 text-center">
        <div>
          <p className="font-display text-[20px] font-bold text-token-primary">Quote not found</p>
          <p className="mt-1 text-[14px] text-token-secondary">This link may have expired or been withdrawn.</p>
        </div>
      </div>
    )
  }

  const { quote, company, client, items } = q.data

  // Spec 021 groupings. A quote with neither tiers nor add-ons renders exactly as before.
  const tierNames = [...new Set(items.filter((i) => i.tier).map((i) => i.tier!))]
  const baseItems = items.filter((i) => !i.tier && !i.optional)
  const addOns = items.filter((i) => i.optional && !i.tier)
  const hasOptions = tierNames.length > 0 || addOns.length > 0

  // The running total the customer sees. Display only — the server recomputes the real figure at
  // acceptance from the stored rows, so a tampered page cannot change what they are charged.
  const chosenTierItems = items.filter((i) => i.tier && i.tier === tier)
  const liveSubtotal = hasOptions
    ? baseItems.reduce((n, i) => n + Number(i.amount), 0)
      + chosenTierItems.reduce((n, i) => n + Number(i.amount), 0)
      + addOns.filter((a) => picked.has(a.id)).reduce((n, i) => n + Number(i.amount), 0)
    : Number(quote.subtotal)
  const taxRate = Number(quote.subtotal) > 0 ? Number(quote.tax) / Number(quote.subtotal) : 0
  const liveTax = Math.round(liveSubtotal * taxRate * 100) / 100
  const liveTotal = liveSubtotal + liveTax
  const tierSubtotal = (t: string) =>
    items.filter((i) => i.tier === t).reduce((n, i) => n + Number(i.amount), 0)
  const accent = company.accent_color || '#0038a8'
  const cur = quote.currency || 'PHP'
  const companyName = (company.name || 'Your business').trim()
  const clientName = [client.first_name, client.last_name].filter(Boolean).join(' ').trim() || client.organization || 'Customer'
  const accepted = quote.status === 'accepted' || !!quote.accepted_at
  const declined = quote.status === 'declined' || !!quote.declined_at
  const expired = !accepted && !declined && !!quote.valid_until && quote.valid_until.slice(0, 10) < todayIso()

  return (
    <div className="min-h-screen bg-canvas-subtle px-4 py-8 print:bg-token-surface print:py-0 sm:py-12">
      <div className="mx-auto max-w-3xl">
        {/* Brand bar */}
        <div className="mb-5 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2.5">
            {company.logo
              ? <img src={company.logo} alt={companyName} className="h-9 w-9 rounded-[10px] object-cover" />
              : <span className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[16px] font-bold text-white" style={{ background: accent }}>{companyName[0]?.toUpperCase()}</span>}
            <div>
              <p className="font-display text-[17px] font-bold tracking-[-0.02em] text-token-primary">{companyName}</p>
              {company.abn && <p className="text-[11.5px] text-token-secondary">{businessNumberLabel(company)} {company.abn}</p>}
            </div>
          </div>
          <button onClick={() => window.print()} className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-token-subtle bg-token-surface px-3.5 text-[13px] font-semibold text-token-primary hover:bg-canvas-subtle">
            <Download className="h-4 w-4" /> PDF
          </button>
        </div>

        {/* Document */}
        <div className="overflow-hidden rounded-[16px] border border-token-subtle bg-token-surface shadow-[0_1px_3px_rgba(16,24,40,0.06)] print:border-0 print:shadow-none">
          {depositJustPaid && !accepted && (
            <div className="flex items-center gap-2 bg-token-success-surface px-6 py-2.5 text-[13px] font-bold text-token-success"><Check className="h-4 w-4" /> Deposit received — your quote is being accepted…</div>
          )}
          {quote.deposit_paid_at && accepted && (
            <div className="flex items-center gap-2 bg-token-selected px-6 py-2.5 text-[13px] font-bold text-token-success"><Check className="h-4 w-4" /> Deposit of {money(quote.deposit_amount ?? 0, quote.currency ?? 'PHP')} paid — thank you!</div>
          )}
          {accepted && (
            <div className="flex items-center gap-2 bg-token-success-surface px-6 py-2.5 text-[13px] font-bold text-token-success">
              <Check className="h-4 w-4" /> Accepted{quote.signer_name ? ` by ${quote.signer_name}` : ''}{quote.accepted_at ? ` · ${phFormat(new Date(quote.accepted_at), 'date')}` : ''}
            </div>
          )}
          {declined && (
            <div className="bg-token-danger-surface px-6 py-2.5 text-[13px] font-bold text-token-danger">Declined{quote.decline_reason ? ` — ${quote.decline_reason}` : ''}</div>
          )}
          {expired && (
            <div className="bg-token-selected px-6 py-2.5 text-[13px] font-bold text-token-warning">This quote expired on {phFormat(new Date(quote.valid_until!), 'date')}. Please contact {companyName} for an updated quote.</div>
          )}
          <div className="p-6 sm:p-10">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-token-muted">Quote</p>
                <h1 className="font-display mt-1 text-[24px] font-bold tracking-[-0.02em] text-token-primary">{quote.title || 'Quote'}</h1>
              </div>
              <div className="text-right">
                <p className="text-[13px] font-bold tabular-nums text-token-primary">{quote.quote_number}</p>
                {quote.valid_until && <p className="mt-0.5 text-[12px] text-token-secondary">Valid until {phFormat(new Date(quote.valid_until), 'date')}</p>}
              </div>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-6 border-t border-token-subtle pt-5 text-[13px]">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">For</p>
                <p className="mt-1 font-semibold text-token-primary">{clientName}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">From</p>
                <p className="mt-1 font-semibold text-token-primary">{companyName}</p>
                {company.email && <p className="text-[12px] text-token-secondary">{company.email}</p>}
                {company.phone && <p className="text-[12px] text-token-secondary">{company.phone}</p>}
              </div>
            </div>

            {/* Lines */}
            <div className="mt-7 overflow-x-auto">
            <table className="w-full min-w-[420px] border-separate border-spacing-0 text-[13px]">
              <thead>
                <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">
                  <th className="border-b border-token-subtle pb-2 text-left">Description</th>
                  <th className="border-b border-token-subtle pb-2 text-right">Qty</th>
                  <th className="border-b border-token-subtle pb-2 text-right">Unit</th>
                  <th className="border-b border-token-subtle pb-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(hasOptions ? baseItems : items).map((it, i) => (
                  <tr key={i}>
                    <td className="border-b border-token-subtle py-2.5 pr-2 text-token-primary">{it.description}</td>
                    <td className="whitespace-nowrap border-b border-token-subtle py-2.5 pl-2 text-right tabular-nums text-token-secondary">{Number(it.quantity)}{it.unit ? ` ${it.unit}` : ''}</td>
                    <td className="whitespace-nowrap border-b border-token-subtle py-2.5 pl-2 text-right tabular-nums text-token-secondary">{money(it.unit_price, cur)}</td>
                    <td className="whitespace-nowrap border-b border-token-subtle py-2.5 pl-2 text-right font-semibold tabular-nums text-token-primary">{money(it.amount, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {tierNames.length > 0 && (
              <div className="mt-6 print:hidden">
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">Choose an option</p>
                <div className="grid gap-2.5 sm:grid-cols-3">
                  {tierNames.map((t) => {
                    const on = tier === t
                    return (
                      <button
                        key={t}
                        onClick={() => setTier(t)}
                        className={`rounded-[12px] border p-4 text-left transition-colors ${on ? 'border-token-strong bg-token-surface' : 'border-token-subtle hover:bg-token-surface'}`}
                      >
                        <span className="flex items-center justify-between">
                          <span className="text-[14px] font-bold capitalize text-token-primary">{t}</span>
                          {on && <Check className="h-4 w-4" style={{ color: accent }} />}
                        </span>
                        <span className="mt-1 block font-display text-[19px] font-bold tabular-nums" style={{ color: on ? accent : '#20242c' }}>
                          {money(tierSubtotal(t), cur)}
                        </span>
                        <span className="mt-1.5 block space-y-0.5">
                          {items.filter((i) => i.tier === t).map((i) => (
                            <span key={i.id} className="block text-[12px] text-token-secondary">{i.description}</span>
                          ))}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {addOns.length > 0 && (
              <div className="mt-6 print:hidden">
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">Optional extras</p>
                <div className="space-y-1.5">
                  {addOns.map((a) => {
                    const on = picked.has(a.id)
                    return (
                      <button
                        key={a.id}
                        onClick={() => setPicked((prev) => { const n = new Set(prev); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n })}
                        className={`flex w-full items-center gap-3 rounded-[10px] border px-3.5 py-3 text-left transition-colors ${on ? 'border-token-strong bg-token-surface' : 'border-token-subtle hover:bg-token-surface'}`}
                      >
                        <span className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] border ${on ? 'border-transparent' : 'border-token-subtle'}`} style={on ? { background: accent } : undefined}>
                          {on && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <span className="min-w-0 flex-1 text-[13.5px] text-token-primary">{a.description}</span>
                        <span className="flex-none font-semibold tabular-nums text-token-primary">{money(Number(a.amount), cur)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <div className="w-full max-w-[260px] space-y-2 text-[13px]">
                <div className="flex justify-between"><span className="text-token-secondary">Subtotal</span><span className="tabular-nums text-token-primary">{money(accepted ? quote.subtotal : liveSubtotal, cur)}</span></div>
                {(accepted ? quote.tax : liveTax) > 0 && <div className="flex justify-between"><span className="text-token-secondary">{localeFor(company).taxLabel}</span><span className="tabular-nums text-token-primary">{money(accepted ? quote.tax : liveTax, cur)}</span></div>}
                <div className="flex justify-between border-t border-token-subtle pt-2 text-[16px] font-bold"><span className="text-token-primary">Total</span><span className="font-display tabular-nums" style={{ color: accent }}>{money(accepted ? quote.total : liveTotal, cur)}</span></div>
              </div>
            </div>

            {quote.notes && <p className="mt-6 whitespace-pre-wrap rounded-[10px] bg-token-surface p-4 text-[12.5px] text-token-secondary">{quote.notes}</p>}
          </div>

          {/* Accept / decline */}
          {!accepted && !declined && !expired && (
            <div className="print:hidden">
              <AcceptPanel
                accent={accent}
                deposit={(FEATURES.stripePayments ? Number(quote.deposit_amount ?? 0) : 0)}
                currency={quote.currency ?? 'PHP'}
                pending={accept.isPending || decline.isPending || payDeposit.isPending}
                onAccept={(signer, signature) => {
                  if ((FEATURES.stripePayments ? Number(quote.deposit_amount ?? 0) : 0) > 0) payDeposit.mutate({ signer })
                  else accept.mutate({ signer, signature })
                }}
                onDecline={(reason) => decline.mutate({ reason })}
                error={(accept.error || decline.error || payDeposit.error) instanceof Error ? (accept.error || decline.error || payDeposit.error)!.message : null}
              />
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 print:hidden">
          <a href="/portal" className="text-[12.5px] font-semibold text-token-secondary underline hover:text-token-primary">View all your quotes &amp; invoices →</a>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-token-muted"><ShieldCheck className="h-3.5 w-3.5" /> Secured by MySerbisyo · accepting is legally binding</span>
        </div>
      </div>
    </div>
  )
}

function AcceptPanel({ accent, deposit = 0, currency = 'PHP', onAccept, onDecline, pending, error }: { accent: string; deposit?: number; currency?: string; onAccept: (signer: string, signature: string) => void; onDecline: (reason: string) => void; pending: boolean; error: string | null }) {
  const [name, setName] = useState('')
  const [declineOpen, setDeclineOpen] = useState(false)
  const [reason, setReason] = useState('')
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const hasInk = useRef(false)

  const pos = (e: React.PointerEvent) => {
    const c = canvasRef.current!; const r = c.getBoundingClientRect()
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) }
  }
  const start = (e: React.PointerEvent) => { drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current!.getContext('2d')!; ctx.beginPath(); ctx.moveTo(x, y) }
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return
    const { x, y } = pos(e); const ctx = canvasRef.current!.getContext('2d')!
    ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.strokeStyle = '#20242c'; ctx.lineTo(x, y); ctx.stroke(); hasInk.current = true
  }
  const end = () => { drawing.current = false }
  const clear = () => { const c = canvasRef.current!; c.getContext('2d')!.clearRect(0, 0, c.width, c.height); hasInk.current = false }

  const submit = () => {
    if (!name.trim()) return
    const sig = hasInk.current ? canvasRef.current!.toDataURL('image/png') : name.trim()
    onAccept(name.trim(), sig)
  }

  return (
    <div className="border-t border-token-subtle bg-token-surface p-6 sm:p-10">
      <p className="inline-flex items-center gap-2 text-[14px] font-bold text-token-primary"><PenTool className="h-4 w-4" style={{ color: accent }} /> Accept this quote</p>
      <p className="mt-1 text-[13px] text-token-secondary">{deposit > 0 ? `Sign below, pay the ${money(deposit, currency)} deposit securely by card, and the job is locked in.` : 'Sign below and we’ll be in touch to schedule the work.'}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-[12.5px] font-semibold text-token-secondary">Your full name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith"
            className="mt-1.5 h-10 w-full rounded-[10px] border border-token-subtle bg-token-surface px-3 text-[14px] outline-none focus:border-[color:var(--a)]" style={{ ['--a' as string]: accent }} />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[12.5px] font-semibold text-token-secondary">Signature</label>
            <button onClick={clear} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-token-secondary hover:text-token-primary"><Eraser className="h-3 w-3" /> Clear</button>
          </div>
          <canvas ref={canvasRef} width={520} height={120}
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
            className="mt-1.5 h-[120px] w-full touch-none rounded-[10px] border border-token-subtle bg-token-surface" />
        </div>
      </div>

      {error && <p className="mt-3 text-[13px] font-semibold text-token-danger">{error}</p>}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={submit} disabled={!name.trim() || pending}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-[12px] px-8 text-[15px] font-bold text-white transition-opacity disabled:opacity-50"
          style={{ background: accent }}>
          {pending ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : deposit > 0 ? <><Check className="h-4 w-4" /> Accept &amp; pay {money(deposit, currency)} deposit</> : <><Check className="h-4 w-4" /> Accept &amp; sign</>}
        </button>
        {!declineOpen && (
          <button onClick={() => setDeclineOpen(true)} disabled={pending} className="text-[13px] font-semibold text-token-secondary hover:text-token-danger">Decline quote</button>
        )}
      </div>

      {declineOpen && (
        <div className="mt-4 rounded-[10px] border border-token-danger bg-token-danger-surface p-4">
          <label className="text-[12.5px] font-semibold text-token-secondary">Reason for declining (optional)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            className="mt-1.5 w-full rounded-[10px] border border-token-subtle bg-token-surface px-3 py-2 text-[13.5px] outline-none focus:border-token-danger" placeholder="Too expensive, going another way, …" />
          <div className="mt-2.5 flex gap-2">
            <button onClick={() => onDecline(reason.trim())} disabled={pending}
              className="inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-token-danger px-4 text-[13px] font-bold text-token-inverse disabled:opacity-50">Confirm decline</button>
            <button onClick={() => setDeclineOpen(false)} className="text-[13px] font-semibold text-token-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
